/**
 * Orgs Store
 *
 * Zustand store backing the staff-admin org picker. Holds the list of
 * organizations the current staff user may select and manage, fetched from the
 * tenant-management backend (`GET /api/tenant-management/orgs`).
 *
 * This store owns only the **list** of selectable orgs. The currently-selected
 * target org is owned by the users store (`useUsersStore.targetOrgId`), which is
 * the single source of truth for which org every member operation scopes to.
 * Keeping selection there avoids duplicate "selected org" state and keeps the
 * two stores decoupled.
 *
 * Uses the OrgsApiClient from the registry.
 *
 * @module stores/useOrgsStore
 */

import { create, type StateCreator } from 'zustand';
import type { OrgsApiClient } from '../api/OrgsApiClient';
import type { Org } from '../types';
import { orgsClient as defaultOrgsClient } from '../api/registry';
import { formatErrorMessage } from '../utils';

// =============================================================================
// Types
// =============================================================================

/**
 * Orgs store state shape.
 */
export interface OrgsState {
  // State
  orgs: Org[];
  isLoading: boolean;
  error: Error | null;
  /** True once a successful load has completed (even if it returned []). */
  isLoaded: boolean;

  // Actions
  loadOrgs: () => Promise<void>;
  clearError: () => void;
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Creates the orgs store state creator with an optional API client override.
 *
 * @param orgsClient - Optional orgs client override (defaults to registry client)
 * @returns Zustand state creator function
 */
export function createOrgsStoreCreator(
  orgsClient: OrgsApiClient = defaultOrgsClient,
): StateCreator<OrgsState> {
  return (set) => ({
    // Initial state
    orgs: [],
    isLoading: false,
    error: null,
    isLoaded: false,

    /**
     * Load Orgs - Fetches the list of selectable organizations.
     *
     * 1. Set isLoading=true, clear error
     * 2. Call listOrgs on the orgs client
     * 3. On success, set orgs and mark isLoaded
     * 4. On failure, set error and preserve any existing list
     */
    loadOrgs: async () => {
      set({ isLoading: true, error: null });

      try {
        const { orgs } = await orgsClient.listOrgs();
        set({ orgs, isLoading: false, isLoaded: true });
      } catch (error) {
        const formattedMessage = formatErrorMessage(error);
        set((state) => ({
          error: error instanceof Error ? error : new Error(formattedMessage),
          isLoading: false,
          // Preserve any previously-loaded orgs on failure.
          orgs: state.orgs,
        }));
      }
    },

    /**
     * Clear Error - Clears the error (e.g. on retry or dismiss).
     */
    clearError: () => {
      set({ error: null });
    },
  });
}

// =============================================================================
// Store Factory
// =============================================================================

/**
 * Creates an orgs store instance with an optional API client override.
 *
 * @param orgsClient - Optional orgs client for testing
 * @returns Zustand store hook
 */
export function createOrgsStore(orgsClient?: OrgsApiClient) {
  return create<OrgsState>(createOrgsStoreCreator(orgsClient));
}

// =============================================================================
// Default Export
// =============================================================================

/**
 * Default orgs store instance using the registry client.
 */
export const useOrgsStore = createOrgsStore();
